import {
  Arrangement,
  Product,
  SelectedProduct,
} from 'redux/PDP/arrangement/types/arrangement.types';
import { CateringOrderType } from 'components/Catering/EventDetails/types';
import { CateringStoreInformation } from 'redux/catering/catering.types';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { StoreFinderWidget } from 'components/StoreFinder/interfaces/store-finder-page.interfaces';

interface State {
  selectedProducts: SelectedProduct[];
  products: Product[];
  arrangements: Arrangement[];
  eventDetailsInformation: CateringStoreInformation;
  pendingProduct: {
    selectedProduct: SelectedProduct;
    product: Product;
    arrangement: Arrangement;
  } | null;
}

const initialState: State = {
  selectedProducts: [],
  products: [],
  arrangements: [],
  eventDetailsInformation: {
    eventDate: '',
    orderType: 0,
    selectedStoreInformation: {} as StoreFinderWidget,
    zipCode: '',
  },
  pendingProduct: null,
};

const slice = createSlice({
  name: 'cateringArrangements',
  initialState,
  reducers: {
    addProduct: (
      state,
      {
        payload: { selectedProduct, product, arrangement },
      }: PayloadAction<{
        selectedProduct: SelectedProduct;
        product: Product;
        arrangement: Arrangement;
      }>,
    ) => {
      const existingProduct = state.selectedProducts.find(
        (prod) => prod.id === selectedProduct.id,
      );

      if (existingProduct) {
        existingProduct.quantity += selectedProduct.quantity || 1;
      } else {
        state.selectedProducts.push(selectedProduct);
        state.products.push(product);
        state.arrangements.push(arrangement);
      }

      return state;
    },

    deleteProduct: (
      state,
      {
        payload: { id, arrangementId },
      }: PayloadAction<{ id: number; arrangementId: number }>,
    ) => {
      const updatedselectedProducts = state.selectedProducts.filter(
        (item) => item.id !== id,
      );
      const updatedProducts = state.products.filter((item) => item.id !== id);
      const updatedArrangements = state.arrangements.filter(
        (item) => item.id !== arrangementId,
      );

      state.selectedProducts = updatedselectedProducts;
      state.products = updatedProducts;
      state.arrangements = updatedArrangements;
    },

    editQuantity: (
      state,
      { payload }: PayloadAction<{ id: number; type: string }>,
    ) => {
      state.selectedProducts = state.selectedProducts.map((product) => {
        if (product.id === payload.id) {
          let newQuantity = product.quantity;

          if (payload.type === 'add') {
            newQuantity = product.quantity + 1;
          } else if (payload.type === 'subtract') {
            newQuantity = Math.max(1, product.quantity - 1);
          }

          return {
            ...product,
            quantity: newQuantity,
          };
        }
        return product;
      });
    },

    clearCart: (state) => {
      state.selectedProducts = [];
      state.products = [];
      state.arrangements = [];
    },
    addStoreInformation: (
      state,
      {
        payload,
      }: PayloadAction<{
        eventDate: string;
        storeInfo: StoreFinderWidget;
        zipCode: string;
        orderType: CateringOrderType;
      }>,
    ) => {
      state.eventDetailsInformation = {
        eventDate: payload.eventDate,
        orderType: payload.orderType,
        zipCode: payload.zipCode,
        selectedStoreInformation: payload.storeInfo,
      };
    },
    cleanStoreInformation: (state) => {
      state.eventDetailsInformation = {
        eventDate: '',
        orderType: 0,
        selectedStoreInformation: {} as StoreFinderWidget,
        zipCode: '',
      };
    },
    setPendingProduct: (
      state,
      {
        payload,
      }: PayloadAction<{
        selectedProduct: SelectedProduct;
        product: Product;
        arrangement: Arrangement;
      }>,
    ) => {
      state.pendingProduct = payload;
    },
    clearPendingProduct: (state) => {
      state.pendingProduct = null;
    },
  },
});

export const cateringCartReducer = slice.reducer;
export const {
  addProduct,
  deleteProduct,
  editQuantity,
  clearCart,
  addStoreInformation,
  cleanStoreInformation,
  setPendingProduct,
  clearPendingProduct,
} = slice.actions;
